import { initializeApp } from 'firebase/app';
import { getMessaging, onMessage, isSupported, getToken, type Messaging } from 'firebase/messaging';
import axios from 'axios';
import { API_URL } from './constants/config';
import { getLocalData, isMobileDevice, setLocalData } from './utils/helper';
import NotificationPopup from './utils/NotificationPopup';

const FIREBASE_CONFIG = {
  apiKey: 'AIzaSyDYGGzPmWI587sF3iJ2cZ_eZ8Jl6surTlE',
  authDomain: 'fairdee.firebaseapp.com',
  databaseURL: 'https://fairdee.firebaseio.com',
  projectId: 'fairdee',
  storageBucket: 'fairdee.appspot.com',
  messagingSenderId: '670918406847',
  appId: '1:670918406847:web:22ba3c76124d79c8ac0c0a',
  measurementId: 'G-CEWB28PT4T',
};

/** Removes the pre-'/firebase-push/' registration, which controlled every page on the origin. */
async function unregisterRootScopeWorker(): Promise<void> {
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.filter((r) => new URL(r.scope).pathname === '/').map((r) => r.unregister()));
  } catch {
    // Best effort. A registration we fail to drop costs startup time, never correctness.
  }
}

const PushNotification = (() => {
  let messaging: Messaging | undefined = undefined;
  let swRegistration: Promise<ServiceWorkerRegistration | undefined> = Promise.resolve(undefined);
  const firebaseApp = initializeApp(FIREBASE_CONFIG);

  // `init()` is called from the router as soon as a session exists, which can beat this
  // setup. Awaiting the same promise means it waits for `messaging` and the registration
  // instead of returning early and never sending a token.
  const ready = (async () => {
    if (await isSupported()) {
      messaging = getMessaging(firebaseApp);

      onMessage(messaging, (payload) => {
        const message = payload.data?.body ?? '',
          dl = decodeURIComponent(payload.data?.dl ?? '');
        if (!isMobileDevice()) {
          NotificationPopup.open(message, dl);
        }
      });

      // Scope '/firebase-push/', a path nothing navigates to, so this worker controls no
      // pages. A worker that controls a page has to be running before the browser commits a
      // navigation to it, and starting this one costs about a second: the document's Queueing
      // read 1.04 s with the old root-scope registration and 4.24 ms with none (2026-09-19).
      // Push is delivered to the registration rather than through page scope, so notifications
      // are unaffected — getToken() below is handed this registration explicitly.
      swRegistration = navigator.serviceWorker
        .register('/firebase-push/firebase-messaging-sw.js', { scope: '/firebase-push/' })
        .catch(() => undefined);

      // Drop the root-scope registration every existing install still carries. Left alone it
      // keeps controlling every page and keeps costing a second per navigation.
      void unregisterRootScopeWorker();
    }
  })();

  async function init() {
    // Callers treat this as fire-and-forget (Router.tsx), so a rejection here would surface as
    // an unhandled promise rejection and, with Sentry installed, as a reported error.
    await ready.catch(() => undefined);
    if (!messaging) return;

    const firebaseToken = getLocalData('firebaseToken');
    // Without this the SDK registers its own copy at the default root scope, undoing the
    // narrowing above.
    const serviceWorkerRegistration = await swRegistration;
    if (!serviceWorkerRegistration) return;
    getToken(messaging, { serviceWorkerRegistration })
      .then((currentToken) => {
        if (currentToken !== firebaseToken) {
          setLocalData('firebaseToken', currentToken);
          sendTokenToServer(currentToken);
        }
      })
      .catch(() => {
        // Token retrieval failed (e.g. permission denied); ignore.
      });
  }

  function sendTokenToServer(currentToken: string) {
    // Inlined from the legacy client (its only `communicator` call site).
    axios
      .post<{ id?: string | number }>(`${API_URL}communicator/firebase-registration-token`, {
        token: currentToken,
        app_context: 'agent_web',
        device_metadata: JSON.stringify({
          platform: window.navigator.platform,
          appVersion: window.navigator.appVersion,
          userAgent: window.navigator.userAgent,
        }),
      })
      .then(function ({ data: response }) {
        if (!response.id) {
          setLocalData('firebaseToken', null);
        }
      });
  }

  function unregister() {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .getRegistrations()
        .then((registrations) => {
          registrations.map((registration) => {
            registration.unregister().catch(() => {});
          });
        })
        .catch(() => {});
    }
  }

  return {
    init,
    unregister,
  };
})();

export default PushNotification;
